import * as React from "react";
import { Check, ChevronDown, ChevronRight, Circle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { MIN_VISIBLE_MS, type UIStep } from "@/lib/chat-steps";

// WS5 — the chat reasoning-steps strip. Fills the silent gap between submit and
// first token with a calm, animated vertical list of the pipeline stages the
// worker streams (STREAM_STEPS on). Once the answer arrives it collapses to a
// subtle "Reasoning" disclosure rendered above the prose.
//
// Flag-OFF prod path: when the worker never emits step events the parent
// (MessageBubble) never mounts this — it renders today's typing-dots instead.
// So this component only appears when `steps.length > 0`.
//
// Calm Institutional visual language: pending = muted, running = brand-blue
// spinner, done = success-green check. GP-blue used sparingly (only the active
// spinner). Honors prefers-reduced-motion via a css guard on the reveal
// keyframe (.chat-step-item) — the spinner itself is a functional affordance,
// not decoration, so it keeps turning even under reduced motion.

// The worker's verify stage: no content flows until the whole draft is verified
// (VERIFY_PASS), so its `running` state is an intentionally indeterminate wait —
// render it as a labeled spinner, NEVER a fake progress bar. `id` is the stable
// machine key from steps.js (tier-independent), so matching on it is safe.
const VERIFY_STEP_ID = "verify";

function StepIcon({ status, isVerify }: { status: UIStep["status"]; isVerify: boolean }) {
  if (status === "done") {
    return <Check className="size-3.5 shrink-0 text-success" aria-hidden />;
  }
  if (status === "running") {
    // brand-blue active spinner — indeterminate for verify, same affordance for
    // every running stage (no fake progress bar anywhere).
    return (
      <Loader2
        className="size-3.5 shrink-0 animate-spin text-brand"
        aria-hidden
        data-verify={isVerify ? "true" : undefined}
      />
    );
  }
  // pending — a dim, hollow circle
  return <Circle className="size-3.5 shrink-0 text-muted-foreground/40" aria-hidden />;
}

function stepBadgeVariant(status: UIStep["status"]): "outline" | "secondary" | "success" {
  if (status === "done") return "success";
  if (status === "running") return "secondary";
  return "outline";
}

function StepRow({ step, reduceMotion }: { step: UIStep; reduceMotion: boolean }) {
  const isVerify = step.id === VERIFY_STEP_ID;
  return (
    <li
      className={cn(
        "chat-step-item flex items-center gap-2 py-0.5 text-xs",
        !reduceMotion && "chat-step-item-anim",
      )}
    >
      <StepIcon status={step.status} isVerify={isVerify} />
      <span
        className={cn(
          "min-w-0 flex-1 truncate",
          step.status === "done"
            ? "text-muted-foreground"
            : step.status === "running"
              ? "font-medium text-foreground"
              : "text-muted-foreground/70",
        )}
      >
        {step.label}
      </span>
      {step.detail ? (
        <Badge
          variant={stepBadgeVariant(step.status)}
          className="shrink-0 px-1.5 py-0 font-mono text-[9px] uppercase tracking-wider"
        >
          {step.detail}
        </Badge>
      ) : null}
    </li>
  );
}

export function ChatStepStrip({
  steps,
  // While the answer is still streaming, keep the strip expanded. Once the
  // answer has arrived (streaming === false) collapse it to a "Reasoning"
  // disclosure, default-collapsed but re-expandable.
  streaming = true,
}: {
  steps: UIStep[];
  streaming?: boolean;
}) {
  // Read the reduced-motion preference once on mount (matches TrustConsole).
  // The test setup stubs matchMedia → matches:false, so the anim class is on
  // by default in tests unless a test overrides the stub.
  const [reduceMotion, setReduceMotion] = React.useState(false);
  React.useEffect(() => {
    try {
      setReduceMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch {
      /* non-browser env — leave motion on */
    }
  }, []);

  // Min-visible-duration hold: a stage can flip running→done in <300ms. To keep
  // a just-started stage's spinner visible for ~MIN_VISIBLE_MS we tick a clock
  // while any step is younger than the threshold, and treat such a step as
  // still "running" for display even if the reducer already marked it done.
  const [, setTick] = React.useState(0);
  const now = Date.now();
  const hasYoungStep = steps.some(
    (s) => s.status === "done" && now - s.startedAt < MIN_VISIBLE_MS,
  );
  React.useEffect(() => {
    if (!hasYoungStep) return;
    const t = window.setTimeout(() => setTick((n) => n + 1), MIN_VISIBLE_MS);
    return () => window.clearTimeout(t);
  }, [hasYoungStep, steps.length]);

  // Apply the min-visible hold for display: a done step still inside its
  // min-visible window renders as running so its spinner doesn't flash by.
  const displaySteps = React.useMemo<UIStep[]>(
    () =>
      steps.map((s) =>
        s.status === "done" && now - s.startedAt < MIN_VISIBLE_MS
          ? { ...s, status: "running" as const }
          : s,
      ),
    // `now` intentionally excluded — re-derived each render via the tick above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps],
  );

  // Collapse state for the post-answer "Reasoning" disclosure. Default: open
  // while streaming, collapsed once the answer arrives.
  const [expanded, setExpanded] = React.useState(true);
  const prevStreaming = React.useRef(streaming);
  React.useEffect(() => {
    // On the streaming → done transition, collapse (default). Only auto-collapse
    // once, on the edge, so a user who re-expands after completion stays open.
    if (prevStreaming.current && !streaming) setExpanded(false);
    prevStreaming.current = streaming;
  }, [streaming]);

  if (displaySteps.length === 0) return null;

  const list = (
    <ul className="chat-step-list flex flex-col gap-0.5" data-testid="chat-step-list">
      {displaySteps.map((s) => (
        <StepRow key={s.id} step={s} reduceMotion={reduceMotion} />
      ))}
    </ul>
  );

  // While streaming: render the open list directly (no disclosure chrome — this
  // IS the pending state, it should read as live activity, not a foldout).
  if (streaming) {
    return (
      <div className="chat-step-strip" data-testid="chat-step-strip" aria-live="polite">
        {list}
      </div>
    );
  }

  // Post-answer: a subtle, collapsed-by-default "Reasoning" disclosure. Collapse
  // mechanism copied from AccordionRenderer (chevron + aria-expanded + a
  // max-height/opacity CSS transition on the panel).
  return (
    <div className="chat-step-strip chat-step-disclosure mb-2" data-testid="chat-step-strip">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex items-center gap-1.5 rounded-sm text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {expanded ? (
          <ChevronDown className="size-3.5" aria-hidden />
        ) : (
          <ChevronRight className="size-3.5" aria-hidden />
        )}
        Reasoning
        <span className="font-normal text-muted-foreground/70">
          · {displaySteps.length} step{displaySteps.length === 1 ? "" : "s"}
        </span>
      </button>
      <div
        className={cn(
          "chat-step-disclosure-panel overflow-hidden transition-all duration-200",
          expanded ? "mt-1.5 max-h-96 opacity-100" : "max-h-0 opacity-0",
        )}
        data-open={expanded ? "true" : "false"}
      >
        {list}
      </div>
    </div>
  );
}
